import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import {
  cancelProject,
  archiveProject,
  continueProject,
  createAnalysis,
  createProject,
  deleteAnalysis,
  deleteProject,
  enhanceAnalysis,
  estimateProject,
  expandProject,
  getAnalysesList,
  getAnalysis,
  getClusterPhrases,
  getPresets,
  getProjectClusters,
  getProjectDetail,
  getProjectLogs,
  getProjectsList,
  getWordstatExpense,
  toggleClusterHidden,
  togglePhraseHidden,
} from "./api";
import type {
  AnalysisCreate,
  AnalysisFull,
  AnalysisShort,
  ClusterOut,
  ClusterPhraseOut,
  ClustersListResponse,
  EstimateIn,
  EstimateOut,
  ExpandRequest,
  ExpensePeriod,
  PhrasesListResponse,
  PresetsResponse,
  ProjectCreate,
  ProjectCreateOut,
  ProjectDetailResponse,
  ProjectListResponse,
  ProjectLogOut,
  ProjectOut,
  ToggleClusterHiddenRequest,
  TogglePhraseHiddenRequest,
  WordstatExpenseOut,
} from "./types";

// --- query keys ---

export const QK = {
  projects: ["projects"] as const,
  project: (id: string) => ["project", id] as const,
  projectClusters: (id: string) => ["project", id, "clusters"] as const,
  projectPhrases: (pid: string, cid: string) =>
    ["project", pid, "cluster", cid, "phrases"] as const,
  projectLogs: (id: string) => ["project", id, "logs"] as const,
  analyses: (pid: string) => ["project", pid, "analyses"] as const,
  analysis: (aid: string) => ["analysis", aid] as const,
  presets: ["projects", "presets"] as const,
  expense: (p: ExpensePeriod) => ["me", "wordstat-expense", p] as const,
};

// ---------- presets / estimate ----------

export function usePresetsQuery() {
  return useQuery<PresetsResponse>({
    queryKey: QK.presets,
    queryFn: getPresets,
    staleTime: Infinity,
  });
}

export function useEstimateMutation() {
  return useMutation<EstimateOut, Error, EstimateIn>({
    mutationFn: estimateProject,
  });
}

// ---------- projects list / detail ----------

export function useProjectsListQuery(params: {
  archived?: boolean | null;
  q?: string | null;
  limit?: number;
  offset?: number;
} = {}) {
  return useQuery<ProjectListResponse>({
    queryKey: [...QK.projects, params],
    queryFn: () => getProjectsList(params),
    staleTime: 10_000,
  });
}

export function useProjectDetailQuery(
  id: string,
  options?: Partial<UseQueryOptions<ProjectDetailResponse>>,
) {
  return useQuery<ProjectDetailResponse>({
    queryKey: QK.project(id),
    queryFn: () => getProjectDetail(id),
    staleTime: 5_000,
    ...options,
  });
}

export function useProjectClustersQuery(
  id: string,
  params: { show_hidden?: boolean } = {},
  enabled = true,
) {
  return useQuery<ClustersListResponse>({
    queryKey: [...QK.projectClusters(id), params],
    queryFn: () => getProjectClusters(id, params),
    enabled,
    staleTime: 5_000,
  });
}

export function useClusterPhrasesQuery(
  pid: string,
  cid: string,
  params: {
    q?: string;
    sort?: "count_desc" | "count_asc";
    limit?: number;
    offset?: number;
    show_hidden?: boolean;
  },
  enabled = true,
) {
  return useQuery<PhrasesListResponse>({
    queryKey: [...QK.projectPhrases(pid, cid), params],
    queryFn: () => getClusterPhrases(pid, cid, params),
    enabled,
  });
}

export function useProjectLogsQuery(id: string, enabled = true) {
  return useQuery<ProjectLogOut[]>({
    queryKey: QK.projectLogs(id),
    queryFn: () => getProjectLogs(id),
    enabled,
    refetchInterval: 5_000,
  });
}

// ---------- mutations: project ----------

export function useCreateProjectMutation() {
  const qc = useQueryClient();
  return useMutation<ProjectCreateOut, Error, ProjectCreate>({
    mutationFn: createProject,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.projects }),
  });
}

export function useCancelProjectMutation() {
  const qc = useQueryClient();
  return useMutation<ProjectOut, Error, string>({
    mutationFn: cancelProject,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: QK.projects });
      qc.invalidateQueries({ queryKey: QK.project(p.id) });
    },
  });
}

export function useArchiveProjectMutation() {
  const qc = useQueryClient();
  return useMutation<ProjectOut, Error, { id: string; on: boolean }>({
    mutationFn: ({ id, on }) => archiveProject(id, on),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.projects }),
  });
}

export function useDeleteProjectMutation() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: deleteProject,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.projects }),
  });
}

export function useContinueProjectMutation() {
  const qc = useQueryClient();
  return useMutation<ProjectOut, Error, string>({
    mutationFn: continueProject,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: QK.projects });
      qc.invalidateQueries({ queryKey: QK.project(p.id) });
    },
  });
}

export function useExpandProjectMutation() {
  const qc = useQueryClient();
  return useMutation<
    ProjectOut,
    Error,
    { id: string; body: ExpandRequest }
  >({
    mutationFn: ({ id, body }) => expandProject(id, body),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: QK.projects });
      qc.invalidateQueries({ queryKey: QK.project(p.id) });
      qc.invalidateQueries({ queryKey: QK.projectClusters(p.id) });
    },
  });
}

export function useTogglePhraseHiddenMutation(
  projectId: string,
  clusterId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    ClusterPhraseOut,
    Error,
    { phraseId: number; body: TogglePhraseHiddenRequest }
  >({
    mutationFn: ({ phraseId, body }) =>
      togglePhraseHidden(projectId, clusterId, phraseId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: QK.projectPhrases(projectId, clusterId),
      });
      // Кластер тоже может измениться (счётчики), на всякий случай.
      qc.invalidateQueries({ queryKey: QK.projectClusters(projectId) });
    },
  });
}

export function useToggleClusterHiddenMutation(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    ClusterOut,
    Error,
    { clusterId: string; body: ToggleClusterHiddenRequest }
  >({
    mutationFn: ({ clusterId, body }) =>
      toggleClusterHidden(projectId, clusterId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.projectClusters(projectId) });
      // Аналитики после скрытия кластера тоже устаревают.
      qc.invalidateQueries({ queryKey: QK.analyses(projectId) });
    },
  });
}

// ---------- analyses ----------

export function useAnalysesQuery(pid: string, enabled = true) {
  return useQuery<AnalysisShort[]>({
    queryKey: QK.analyses(pid),
    queryFn: () => getAnalysesList(pid),
    enabled,
  });
}

export function useAnalysisQuery(aid: string, enabled = true) {
  return useQuery<AnalysisFull>({
    queryKey: QK.analysis(aid),
    queryFn: () => getAnalysis(aid),
    enabled,
    refetchInterval: (q) => {
      // Пока статус enhancing — поллим каждые 3 сек, чтобы UI догонял.
      const data = q.state.data as AnalysisFull | undefined;
      return data?.status === "enhancing" ? 3_000 : false;
    },
  });
}

export function useCreateAnalysisMutation(pid: string) {
  const qc = useQueryClient();
  return useMutation<AnalysisFull, Error, AnalysisCreate>({
    mutationFn: (body) => createAnalysis(pid, body),
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: QK.analyses(pid) });
      qc.setQueryData(QK.analysis(a.id), a);
    },
  });
}

export function useEnhanceAnalysisMutation(aid: string) {
  const qc = useQueryClient();
  return useMutation<AnalysisFull, Error, void>({
    mutationFn: () => enhanceAnalysis(aid),
    onSuccess: (a) => qc.setQueryData(QK.analysis(aid), a),
  });
}

export function useDeleteAnalysisMutation(pid: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (aid) => deleteAnalysis(aid),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.analyses(pid) }),
  });
}

// ---------- expense ----------

export function useWordstatExpenseQuery(period: ExpensePeriod) {
  return useQuery<WordstatExpenseOut>({
    queryKey: QK.expense(period),
    queryFn: () => getWordstatExpense(period),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}
